import { useEffect } from 'react';
import { Toaster, toast } from 'sonner';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE TOAST LAYER
 *
 * A toast is a passive notice — it reports something that has already
 * happened. It should therefore cost the reader nothing, and this one was
 * costing them the top bar: sonner's fixed container sits above everything at
 * `z-index: 999999999`, so search, notifications, the calendar and the theme
 * toggle were all unclickable for as long as a toast was up. The only ways out
 * were waiting or aiming at a close button.
 *
 * Three changes, and all three are needed:
 *
 *   IT CANNOT BE CLICKED. `pointer-events: none` in `index.css`, on both the
 *   container and the toasts. Clicks land on whatever is underneath, as if the
 *   toast were not there.
 *
 *   IT CLEARS ON ANY CLICK. Below. Since nothing can be clicked THROUGH it,
 *   dismissing is no longer something to aim at — carrying on with the work is
 *   what makes it go.
 *
 *   IT SITS BELOW THE TOP BAR. The 68px offset clears the 56px header with a
 *   gap, so it no longer covers the controls at all, whatever the rules above
 *   say about clicks reaching them.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Clear of the 56px header, with a 12px gap — see `AppShell`. */
const HEADER_CLEARANCE = '68px';

export function ToastLayer() {
  useEffect(() => {
    /**
     * Capture phase, and `pointerdown` rather than `click`.
     *
     * Capture so nothing can stop the event before it arrives — a dialog or a
     * table row calling `stopPropagation` would otherwise leave toasts stuck
     * on exactly the screens with the most going on. `pointerdown` because it
     * fires on touch as well as mouse, and at the START of the gesture, so the
     * notice is gone by the time the press lands.
     *
     * The click that CAUSED a toast cannot dismiss it: `pointerdown` runs
     * before the handler that creates it, and every one of them is raised
     * after an await.
     */
    const dismissAll = () => toast.dismiss();

    /**
     * Escape only, out of the keyboard.
     *
     * Dismissing on any key would delete an error message under the fingers of
     * somebody who had started typing a correction to it — the one moment the
     * message is most needed.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismissAll();
    };

    window.addEventListener('pointerdown', dismissAll, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('pointerdown', dismissAll, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  return (
    <Toaster
      richColors
      position="top-right"
      offset={HEADER_CLEARANCE}
      /**
       * No `closeButton`. It cannot be clicked now — the layer takes no
       * pointer events — so leaving it in would draw a control that does
       * nothing, which is worse than the problem it was there to solve.
       */
      duration={3500}
    />
  );
}
